/**
 * Admin API: the account list and impersonation.
 */
import apiClient, { ApiResponse } from '../client';
import type { AdminProductListResult } from './products';

export type AdminUserRole = 'USER' | 'ADMIN' | 'CUSTOMER_ADMIN';

export const ADMIN_USER_ROLES: AdminUserRole[] = [
  'USER',
  'ADMIN',
  'CUSTOMER_ADMIN',
];

/** One account in the admin user list; never carries a password hash. */
export interface AdminUserListItem {
  id: string;
  email: string;
  name: string | null;
  role: AdminUserRole;
  shirteeDesignerId: number | null;
  createdAt: string;
  /** Live (not deleted) designs of the account. */
  designCount: number;
}

export interface AdminUserQuery {
  /** Matches email and name; a number also matches the designer id. */
  search?: string;
  role?: AdminUserRole;
  page?: number;
  limit?: number;
}

export interface AdminUserListResult {
  data: AdminUserListItem[];
  meta: AdminProductListResult['meta'];
}

export interface StartImpersonationInput {
  targetUserId: string;
  /** The admin's own password, re-entered as confirmation. */
  password: string;
}

export interface StartImpersonationResult {
  /** One-time code for `/impersonate#code=…`. */
  code: string;
  expiresInSeconds: number;
}

/**
 * Get all accounts with their design count (paginated, searchable)
 */
export async function getAdminUsers(
  query?: AdminUserQuery
): Promise<AdminUserListResult> {
  const params = new URLSearchParams();
  if (query?.search) params.append('search', query.search);
  if (query?.role) params.append('role', query.role);
  if (query?.page) params.append('page', String(query.page));
  if (query?.limit) params.append('limit', String(query.limit));

  const response = await apiClient.get<ApiResponse<AdminUserListResult>>(
    `/admin/users?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Starts an impersonation of another account after re-checking the admin's
 * password. Returns a one-time code that the new tab exchanges for a token.
 */
export async function startImpersonation(
  input: StartImpersonationInput
): Promise<StartImpersonationResult> {
  const response = await apiClient.post<ApiResponse<StartImpersonationResult>>(
    '/admin/impersonation',
    input
  );
  return response.data.data;
}
